import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

const KEY = "aa_ops_theme";
const ThemeContext = createContext({
  theme: "system",
  resolvedTheme: "light",
  setTheme: () => {},
});

const applyClass = (resolved) => {
  const el = document.documentElement;
  if (resolved === "dark") el.classList.add("dark");
  else el.classList.remove("dark");
};

const resolveSystem = () =>
  window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark" : "light";

const readInitial = () => {
  try {
    const v = localStorage.getItem(KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch (e) { /* ignore */ }
  return "system";
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(readInitial);
  const [resolvedTheme, setResolvedTheme] = useState(
    () => (theme === "system" ? resolveSystem() : theme),
  );

  const setTheme = useCallback((next) => {
    setThemeState(next);
    try { localStorage.setItem(KEY, next); } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    const nextResolved = theme === "system" ? resolveSystem() : theme;
    setResolvedTheme(nextResolved);
    applyClass(nextResolved);
  }, [theme]);

  useEffect(() => {
    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      const r = mq.matches ? "dark" : "light";
      setResolvedTheme(r);
      applyClass(r);
    };
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
